import React from "react"
import { Linking, ScrollView, View } from "react-native"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { Body, H4, Small } from "@/components/standard/BFText"
import { RequestItem } from "./RequestItem"
import { FilePortalCard } from "./FilePortalCard"
import type { MilestoneTaskType, MilestoneType } from "@/generated/graphql"
import { Separator } from "@/components/reusables/ui/separator"
import { MilestoneStatusBadge } from "@/screens/loans/components/dashboard/milestones/MilestoneStatusBadge"

interface MilestoneRequestListProps {
  milestones: MilestoneType[]
  status: "todo" | "completed" | "up_next"
  emptyMessage?: string
  className?: string
  onTaskPress?: (task: MilestoneTaskType) => void
  portalUrl?: string
  showFilePortal?: boolean
  hubspotLoanId?: string
}

// Status mapping functions from MilestoneItem.tsx

const hasFileUploadTasks = (tasks: MilestoneTaskType[]): boolean => {
  return tasks.some((task) => task.taskType === "file_upload")
}

interface MilestoneRequestItemProps {
  milestone: MilestoneType
  status: "todo" | "completed" | "up_next"
  onTaskPress?: (task: MilestoneTaskType) => void
  isLast?: boolean
  portalUrl?: string
  showFilePortal?: boolean
  hubspotLoanId?: string
}

const MilestoneRequestItem: React.FC<MilestoneRequestItemProps> = ({
  milestone,
  status,
  hubspotLoanId,
  onTaskPress,
  isLast = false,
  portalUrl,
  showFilePortal = false,
}) => {
  const showPortalCard = showFilePortal && hasFileUploadTasks(milestone.tasks)

  const handlePortalPress = () => {
    if (portalUrl) {
      void Linking.openURL(portalUrl).catch((err) => {
        console.error("Failed to open portal URL:", err)
      })
    } else {
      console.warn("No portal URL provided")
    }
  }

  return (
    <View className={"px-2"}>
      <Vertical className="gap-3">
        <Vertical className="flex-1 gap-1">
          <Horizontal className="items-start justify-between">
            <H4 color={"default"} className="mb-1">
              {milestone.name}
            </H4>
            <MilestoneStatusBadge milestone={milestone} />
          </Horizontal>
          {milestone.description && status !== "completed" && (
            <Small color={"default"} className=" mb-2">
              {milestone.description}
            </Small>
          )}
          {status === "todo" && milestone.tasks.length > 0 && (
            <Small className="text-muted-foreground mb-2">
              (Status will update once items are reviewed by Backflip)
            </Small>
          )}
          {/* Portal Card - shown when file upload tasks exist */}
          {showPortalCard && (
            <View>
              <FilePortalCard
                onPress={portalUrl ? handlePortalPress : undefined}
              />
            </View>
          )}
        </Vertical>

        {/* Tasks for this milestone */}
        {milestone.tasks.length > 0 && (
          <View className="bg-card border border-border rounded-md p-4">
            {milestone.tasks.map((task, index) => (
              <RequestItem
                key={task.id}
                task={task}
                status={status}
                hubspotLoanId={hubspotLoanId}
                onPress={
                  onTaskPress
                    ? () => {
                        onTaskPress(task)
                      }
                    : undefined
                }
                showSeparator={index < milestone.tasks.length - 1}
              />
            ))}
          </View>
        )}
      </Vertical>

      {!isLast && (
        <View className="py-4">
          <Separator />
        </View>
      )}
    </View>
  )
}

export const MilestoneRequestList: React.FC<MilestoneRequestListProps> = ({
  milestones,
  status,
  emptyMessage = "No requests found",
  className,
  onTaskPress,
  portalUrl,
  showFilePortal = false,
  hubspotLoanId,
}) => {
  return (
    <ScrollView
      className={className}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ paddingBottom: 20 }}
    >
      <Vertical className="gap-4">
        {/* Milestones List */}
        {!milestones || milestones.length === 0 ? (
          <View className="bg-card border border-border rounded-md p-6">
            <Body color="hint" className="text-center">
              {emptyMessage}
            </Body>
          </View>
        ) : (
          <Vertical className="gap-0">
            {milestones.map((milestone, index) => (
              <MilestoneRequestItem
                showFilePortal={showFilePortal}
                portalUrl={portalUrl}
                hubspotLoanId={hubspotLoanId}
                key={milestone.id}
                milestone={milestone}
                status={status}
                onTaskPress={onTaskPress}
                isLast={index === milestones.length - 1}
              />
            ))}
          </Vertical>
        )}
      </Vertical>
    </ScrollView>
  )
}
